import React, { useState, useEffect } from 'react'

import {
  Avatar,
  Icon,
  Like,
  Dislike,
  Chat,
  classnames,
  Button,
  ConfirmModal,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, number, shape, bool, oneOf, arrayOf } from 'prop-types'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import SmartTooltip from 'Pods/Shared/SmartTooltip/SmartTooltip'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import RepliesList from './Components/RepliesList'
import ReplyForm from './Components/ReplyForm'
import formatDate from './Utils/formatDate'
import useReplies from '../../hooks/useReplies'

function getUserRoleText(userRole) {
  switch (userRole) {
    case 'seller':
      return 'Seller of this model'
    case 'multiple_buyer':
      return 'Returning buyer from this seller'
    case 'buyer':
      return 'Buyer of this model'
    default:
      return 'Community member'
  }
}

function getUserRoleTooltip(userRole) {
  switch (userRole) {
    case 'seller':
      return 'This user is the seller of this model'
    case 'multiple_buyer':
      return 'This user has purchased multiple models from this seller'
    case 'buyer':
      return 'This user has purchased this specific model'
    default:
      return 'This user is part of the CGTrader community'
  }
}

const imageProps = {
  loading: 'lazy',
  decoding: 'async',
}

const DEFAULT_AVATAR = {
  small: {
    url: null,
  },
  url: null,
}

function Rating({
  id,
  content = '',
  userName = 'Anonymous',
  isCurrentUserAdmin = false,
  updatedAt,
  rating = null,
  avatar = DEFAULT_AVATAR,
  anonymous = false,
  userRole = 'community_member',
  isLoggedIn = false,
  replies = EMPTY_ARRAY,
  isInSidebar = false,
  currentUserAvatar = null,
  currentUsername = '',
  productId = null,
  productOwnerUsername = null,
}) {
  const { deleteData, isLoading } = useApiDelete()

  const {
    localReplies,
    replyContent,
    setReplyContent,
    showReplyForm,
    setShowReplyForm,
    handleReplySubmit,
    isLoading: isReplyLoading,
  } = useReplies(replies, productId, id)

  const [isDeleted, setIsDeleted] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  // Relative dates frozen in cached SSR HTML differ from the value recomputed
  // at hydration time. The first client pass re-renders the recomputed relative
  // value; the mismatch is suppressed on the span (React keeps the server
  // text), and after mount we swap in the fresh value. Starting from null
  // guarantees the post-mount setState never bails out via Object.is.
  const [freshDate, setFreshDate] = useState(null)

  useEffect(() => {
    setFreshDate(formatDate(updatedAt))
  }, [updatedAt])

  // Check if current user can reply
  const isCommentAuthor = currentUsername === userName
  const isProductOwner = currentUsername === productOwnerUsername
  const canReply = isLoggedIn && (isCommentAuthor || isProductOwner || isCurrentUserAdmin)

  const isPositiveRating = rating > 0
  const isNegativeRating = rating < 0
  const isNeutralRating = rating === undefined || rating === null
  const trimmedContent = (content ?? '').trim()
  const hasContent = Boolean(trimmedContent.length)

  const placeholderText = (() => {
    if (hasContent) return null
    if (isPositiveRating) return 'The buyer left positive feedback on this model'
    if (isNegativeRating) return 'The buyer left negative feedback on this model'
    return null
  })()

  const shouldShowPlaceholder = Boolean(placeholderText)

  function renderWithOptionalLink(element) {
    if (!anonymous && isLoggedIn) {
      return (
        <a href={`/designers/${userName}`} target="_blank" rel="noopener noreferrer" className="rating__profile-link">
          {element}
        </a>
      )
    }
    return element
  }

  async function handleDeleteConfirmation() {
    const url = isNeutralRating
      ? `/api/internal/comments/${id}`
      : `/api/internal/item_ratings/${id}`

    deleteData({
      path: url,
      onSuccess: () => {
        setIsDeleted(true)
        setShowDeleteModal(false)
        window.showNotification('Review/Comment deleted successfully', { type: 'success' })
      },
      onError: () => {
        setShowDeleteModal(false)
        setIsDeleted(false)
        window.showNotification('Failed to delete Review/Comment', { type: 'error' })
      },
    })
  }

  const onReplySubmit = () => {
    handleReplySubmit(currentUsername, currentUserAvatar)
  }

  function getIcon() {
    if (isPositiveRating) {
      return Like
    }

    if (isNegativeRating) {
      return Dislike
    }

    return Chat
  }

  return (
    <>
      <div
        className={classnames(
          'rating',
          isPositiveRating && 'rating--positive',
          isNegativeRating && 'rating--negative',
          isNeutralRating && 'rating--neutral',
          isDeleted && 'rating--deleted',
        )}
      >
        <div className="rating__header">
          {!anonymous && renderWithOptionalLink(
            <Avatar
              size="40"
              img={avatar?.small?.url}
              username={userName}
              imageProps={imageProps}
            />
          )}
          <div className="rating__info-container">
            {renderWithOptionalLink(
              <span className="rating__username">{userName}</span>
            )}
            <span className="rating__user-role">
              <SmartTooltip
                content={getUserRoleTooltip(userRole)}
                id={`tooltip-user-role-${id}`}
                delayHide={100}
                delayUpdate={100}
                autoPosition
                wrapperTag="span"
              >
                {getUserRoleText(userRole)}
              </SmartTooltip>
            </span>
          </div>
          <div
            className={classnames(
              'rating__icon-container',
              isPositiveRating && 'rating__icon-container--positive',
              isNegativeRating && 'rating__icon-container--negative',
              isNeutralRating && 'rating__icon-container--neutral',
            )}
          >
            <Icon
              svgComponent={getIcon()}
              className="rating__icon"
              color={isNeutralRating ? 'dark' : 'light'}
            />
          </div>
        </div>
        <div className="rating__content">
          {shouldShowPlaceholder ? <em>{placeholderText}</em> : content}
        </div>
        <div className="rating__footer">
          {/* suppressHydrationWarning is load-bearing: the first client pass
              still recomputes the relative date, and without it the text
              mismatch fires onRecoverableError and our renderHelpers fallback
              re-renders the whole container (see vendor/reactRenderHelpers.js).
              The ?? fallback also covers the client-only sidebar mount, where
              there is no server text and null would flash an empty span. */}
          <span className="rating__date" suppressHydrationWarning>{freshDate ?? formatDate(updatedAt)}</span>

          {canReply && isInSidebar && isNeutralRating && (
            <Button
              className="rating__button"
              onClick={() => setShowReplyForm(!showReplyForm)}
              type="ghost"
              actionType="button"
              size="tiny"
              aria-label={`Reply to ${userName}'s comment`}
            >
              Reply
            </Button>
          )}

          {isNeutralRating && localReplies.length > 0 && (
            <span className="cgt-legacy-text-tiny rating__reply-count">
              {localReplies.length} {localReplies.length === 1 ? 'Reply' : 'Replies'}
            </span>
          )}
        </div>
        {isCurrentUserAdmin && (
          <Button
            type="secondary-warning"
            actionType="button"
            customClass="rating__admin-delete-button"
            onClick={() => setShowDeleteModal(true)}
            disabled={isLoading}
            size="small"
          >
            DELETE
          </Button>
        )}

        {showReplyForm && isNeutralRating && (
          <ReplyForm
            replyContent={replyContent}
            onReplyContentChange={(e) => setReplyContent(e.target.value)}
            onSubmit={onReplySubmit}
            userAvatar={currentUserAvatar}
            username={currentUsername}
            isLoading={isReplyLoading}
          />
        )}

        {isInSidebar && isNeutralRating && (
          <RepliesList replies={localReplies} isLoggedIn={isLoggedIn} />
        )}
      </div>
      {showDeleteModal && (
        <ConfirmModal
          header={
            isNeutralRating
              ? 'Are you sure you want to delete this Comment?'
              : 'Are you sure you want to delete this Review?'
          }
          submitText="Delete"
          cancelText="Cancel"
          onSubmit={handleDeleteConfirmation}
          onCancel={() => setShowDeleteModal(false)}
          isSubmitDisabled={isLoading}
        />
      )}
    </>
  )
}

Rating.propTypes = {
  isCurrentUserAdmin: bool,
  content: string,
  userName: string.isRequired,
  updatedAt: string.isRequired,
  rating: number,
  avatar: shape({
    small: shape({
      url: string,
    }),
    url: string,
  }),
  anonymous: bool,
  userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
  isLoggedIn: bool,
  id: number.isRequired,
  replies: arrayOf(shape({
    id: number,
    userId: number,
    content: string,
    updatedAt: string,
    username: string,
    avatar: shape({
      small: shape({
        url: string,
      }),
    }),
  })),
  isInSidebar: bool,
  currentUserAvatar: shape({
    small: shape({
      url: string,
    }),
    url: string,
  }),
  currentUsername: string,
  productId: number,
  productOwnerUsername: string,
}

export default Rating
